import { useAppStore } from "@/store";
import { TopicInfo } from "@/components/item-list/types";
import { removeNoteFromCollection } from "@/actions/removeNoteFromCollection";
import { observer } from "mobx-react-lite";
import { INoteObservable, NotesIndexTuple } from "@/store/note";
import { ReactEventHandler } from "react";
import { useMaybeTabContext } from "@/components/layout/tab-context";
import { useLiveQuery } from "dexie-react-hooks";
import { LensKind, SortByKind } from "@/modules/lenses/types";
import { CollectionPreviewView } from "./collection-preview.view";

interface CollectionPreviewProps {
  collectionTopic: TopicInfo;
  noteId: string;
  onDismiss: () => void;
}

export const CollectionPreview = observer(
  ({ collectionTopic, noteId, onDismiss }: CollectionPreviewProps) => {
    const { store } = useAppStore();
    const tabContext = useMaybeTabContext();

    const collection = store.collections.getCollectionObservableById({
      collectionId: collectionTopic.id,
    });

    const note = store.notes.getNoteObservableById({ noteId });
    const recentNoteResults = useLiveQuery(() =>
      store.notes.search({
        lens: LensKind.All,
        sortBy: SortByKind.LastInteracted,
        limit: 5,
        filter: { collections: [{ ids: [collectionTopic.id], mode: "all" }] },
      })
    );
    const recentNotes = store.notes.getMany(
      recentNoteResults?.map((result: NotesIndexTuple) => result.at(-1) as string) ?? []
    );

    const handleRemove: React.MouseEventHandler = e => {
      if (!note || !collection) return;
      e.stopPropagation();
      removeNoteFromCollection({
        note,
        collection: collection,
        store: store,
      });
      return true;
    };

    const handleOpenInSplit: ReactEventHandler = e => {
      if (!collection) return;
      e.stopPropagation();
      store.navigation.goToCollection({
        collectionId: collection.id,
        openInInactivePane: true,
        activeTabId: tabContext?.activeTab.id,
      });

      return true;
    };

    const handleOpenNote = (note: INoteObservable) => {
      if (!note) return;
      onDismiss();
      store.navigation.goToNote({ noteId: note.id });
    };

    if (!collection) return null;
    return (
      <CollectionPreviewView
        collectionId={collection.id}
        collectionLabel={collection.label}
        collectionDescription={collection.description}
        recentNotes={recentNotes.map(note => ({
          id: note.id,
          title: note.title,
        }))}
        noteId={noteId}
        onRemove={handleRemove}
        onOpenInSplit={handleOpenInSplit}
        onOpenNote={noteId => {
          const note = store.notes.getNoteObservableById({ noteId });
          if (note) handleOpenNote(note);
        }}
      />
    );
  }
);
